import { useMemo } from "react";
import {
  PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer,
} from "recharts";
import { computeHonKitzba } from "../utils/honKitzba";
import { computeHonBreakdown, getFundClass } from "../utils/reportComputations";
import { SummaryCard } from "./UI";
import { CATEGORY_STYLE } from "../utils/fieldMaps";
import { fmtMoney } from "../parsers/xmlParser";
import PortfolioEvolutionChart from "./PortfolioEvolutionChart";

// ── Colors ────────────────────────────────────────────────────────────────────
const CAT_COLORS = {
  "ביטוח מנהלים": "#0d9488",
  "קרן פנסיה":    "#2563eb",
  "ביטוח חיים":   "#e11d48",
  "קופת גמל":     "#d97706",
  "קרן השתלמות":  "#16a34a",
  "קופת גמל להשקעה": "#7c3aed",
  "חיסכון לכל ילד": "#f43f5e",
};

// Deterministic distinct color for a custom (advisor-typed) category name, so
// manually-added products don't all collapse into the same gray slice.
const CUSTOM_CAT_PALETTE = ["#0891b2", "#c026d3", "#65a30d", "#ea580c", "#4f46e5", "#be123c", "#0d9488", "#9333ea"];
function catColor(name) {
  if (CAT_COLORS[name]) return CAT_COLORS[name];
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0;
  return CUSTOM_CAT_PALETTE[h % CUSTOM_CAT_PALETTE.length];
}

const HON_KITZBA_COLORS = {
  "הון תגמולים":        "#16a34a", // Green
  "הון השתלמות":        "#0d9488", // Teal
  "הון פיצויים":        "#22c55e", // Green
  "תיקון 190":          "#84cc16", // Green
  "קצבה (עד 2000)":     "#0f766e", // Dark Teal
  "קצבה משלמת":         "#2563eb", // Blue (matches Pension Fund)
  "קצבה לא משלמת":      "#60a5fa", // Light Blue
  "קצבה פיצויים":       "#e11d48", // Red
};

// ── Helpers ───────────────────────────────────────────────────────────────────
const fmt = (n) => Math.round(n).toLocaleString("he-IL");
const pct = (v, total) => total > 0 ? ((v / total) * 100).toFixed(1) + "%" : "0%";

const CustomTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const { name, value, payload: inner } = payload[0];
  const total = inner?.total;
  return (
    <div style={{
      background: "white", border: "1px solid #e2e8f0", borderRadius: 10,
      padding: "10px 14px", fontSize: 13, direction: "rtl", boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
    }}>
      <div style={{ fontWeight: 700, marginBottom: 4 }}>{name}</div>
      <div style={{ color: "#0f172a" }}>{fmt(value)} ₪</div>
      {total > 0 && <div style={{ color: "#64748b", fontSize: 11 }}>{pct(value, total)} מהסך הכל</div>}
    </div>
  );
};

const CustomLegend = ({ payload, showAmount }) => (
  <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "center", gap: "8px 16px", marginTop: 10 }}>
    {payload?.map((entry, i) => (
      <div key={i} style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 11 }}>
        <div style={{ width: 10, height: 10, borderRadius: "50%", background: entry.color, flexShrink: 0 }} />
        <span style={{ color: "#475569" }}>{entry.value}</span>
        {showAmount && (
          <span style={{ color: "#0f172a", fontWeight: 700 }}>
            {fmt(entry.payload?.value)} ₪
          </span>
        )}
      </div>
    ))}
  </div>
);

// ── Chart card wrapper ────────────────────────────────────────────────────────
function ChartCard({ title, subtitle, children }) {
  return (
    <div style={{
      background: "white", borderRadius: 16, padding: "20px 16px 16px",
      boxShadow: "0 1px 4px rgba(0,0,0,0.07)", border: "1px solid #f1f5f9",
    }}>
      <div style={{ fontSize: 14, fontWeight: 800, color: "#0f172a", marginBottom: 2, textAlign: "center" }}>{title}</div>
      {subtitle && <div style={{ fontSize: 11, color: "#94a3b8", marginBottom: 12, textAlign: "center" }}>{subtitle}</div>}
      {children}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export default function DashboardCharts({ funds, onAlertsClick, history }) {
  // ── 1. Category breakdown ─────────────────────────────────────────────────
  const catData = useMemo(() => {
    const CATEGORIES = ["ביטוח מנהלים", "קרן פנסיה", "ביטוח חיים", "קופת גמל", "קרן השתלמות", "קופת גמל להשקעה", "חיסכון לכל ילד"];
    const total = funds.reduce((s, f) => s + (f.redeem || 0), 0);
    // Include any custom categories present in the funds (e.g. manual products)
    // that aren't in the canonical list, so they get their own slice.
    const allCats = [...CATEGORIES, ...[...new Set(funds.map(f => f.cat).filter(c => c && !CATEGORIES.includes(c)))]];
    return allCats
      .map(cat => ({
        name: cat,
        value: funds.filter(f => f.cat === cat).reduce((s, f) => s + (f.redeem || 0), 0),
        total,
      }))
      .filter(d => d.value > 0);
  }, [funds]);

  // ── 2. הון / קצבה breakdown ───────────────────────────────────────────────
  const honKitzbaData = useMemo(() => {
    // מקור אמת יחיד לחלק ההון — משותף עם כרטיס "ריכוז כספים בהון" ודוח ה-PDF.
    const { tagmulimHon: honTagmulim, hashtalamut: honHishtalmut,
            pitzuyimHon: honPitzuyim, tikun190: hon190, kitzbaUntil2000: honKitzbaUntil2000 } = computeHonBreakdown(funds).totals;
    let kitzbaMeshalemet = 0, kitzbaLoMeshalemet = 0, kitzbaPitzuyim = 0;

    funds.forEach(f => {
      const hk = computeHonKitzba(f);
      kitzbaPitzuyim += hk.kitzbaPitzuyim || 0;

      const isLoMeshalemet = getFundClass(f) === "קצבה לא משלמת";

      if (isLoMeshalemet) {
        kitzbaLoMeshalemet += (hk.kitzba || 0) - (hk.kitzbaPreSettlement || 0);
      } else {
        kitzbaMeshalemet += (hk.kitzba || 0) - (hk.kitzbaPreSettlement || 0);
      }
    });

    const raw = [
      { name: "הון תגמולים",     value: honTagmulim },
      { name: "הון השתלמות",     value: honHishtalmut },
      { name: "הון פיצויים",     value: honPitzuyim },
      { name: "תיקון 190",       value: hon190 },
      { name: "קצבה (עד 2000)",  value: honKitzbaUntil2000 },
      { name: "קצבה משלמת",      value: kitzbaMeshalemet },
      { name: "קצבה לא משלמת",   value: kitzbaLoMeshalemet },
      { name: "קצבה פיצויים",    value: kitzbaPitzuyim },
    ].filter(d => d.value > 0);

    const total = raw.reduce((s, d) => s + d.value, 0);
    return raw.map(d => ({ ...d, total }));
  }, [funds]); // eslint-disable-line

  // ── 3. Company breakdown (top 8 + other) ─────────────────────────────────
  const companyData = useMemo(() => {
    const map = {};
    funds.forEach(f => {
      const name = f.companyName || "אחר";
      map[name] = (map[name] || 0) + (f.redeem || 0);
    });
    const sorted = Object.entries(map)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value);

    const top = sorted.slice(0, 7);
    const rest = sorted.slice(7).reduce((s, d) => s + d.value, 0);
    if (rest > 0) top.push({ name: "אחרות", value: rest });

    const total = top.reduce((s, d) => s + d.value, 0);
    return top.map(d => ({ ...d, total }));
  }, [funds]);

  const COMPANY_COLORS = [
    "#2563eb","#0d9488","#d97706","#7c3aed","#e11d48",
    "#0891b2","#16a34a","#94a3b8",
  ];

  // ── Totals for summary strip ───────────────────────────────────────────────
  const grandTotal = funds.reduce((s, f) => s + (f.redeem || 0), 0);

  return (
    <div dir="rtl" style={{ padding: "0 0 16px" }}>

      {/* 3 charts */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 14 }}>

        {/* Chart 1 — סוגי מוצרים */}
        <ChartCard title="סוגי מוצרים">
          <ResponsiveContainer width="100%" height={260}>
            <PieChart>
              <Pie data={catData} dataKey="value" nameKey="name"
                cx="50%" cy="45%" innerRadius={55} outerRadius={90}
                paddingAngle={3}>
                {catData.map((entry) => (
                  <Cell key={entry.name} fill={catColor(entry.name)} />
                ))}
              </Pie>
              <Tooltip content={<CustomTooltip />} />
              <Legend content={(props) => <CustomLegend {...props} showAmount />} />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>

        {/* Chart 2 — הון / קצבה */}
        <ChartCard title="הון וקצבה">
          <ResponsiveContainer width="100%" height={260}>
            <PieChart>
              <Pie data={honKitzbaData} dataKey="value" nameKey="name"
                cx="50%" cy="45%" innerRadius={55} outerRadius={90}
                paddingAngle={3}>
                {honKitzbaData.map((entry) => (
                  <Cell key={entry.name} fill={HON_KITZBA_COLORS[entry.name] || "#94a3b8"} />
                ))}
              </Pie>
              <Tooltip content={<CustomTooltip />} />
              <Legend content={(props) => <CustomLegend {...props} showAmount />} />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>

        {/* Chart 3 — חברות */}
        <ChartCard title="חלוקה לפי חברה">
          <ResponsiveContainer width="100%" height={260}>
            <PieChart>
              <Pie data={companyData} dataKey="value" nameKey="name"
                cx="50%" cy="50%" innerRadius={55} outerRadius={90}
                paddingAngle={3}>
                {companyData.map((_, i) => (
                  <Cell key={i} fill={COMPANY_COLORS[i % COMPANY_COLORS.length]} />
                ))}
              </Pie>
              <Tooltip content={<CustomTooltip />} />
              <Legend content={(props) => <CustomLegend {...props} showAmount />} />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>

        {/* Chart 4 — התפתחות תיק */}
        {history && history.length >= 2 && (
          <PortfolioEvolutionChart history={history} />
        )}

      </div>
    </div>
  );
}
